import { observable, set, computed } from 'mobx';
import { DiscountTypes } from '~api/utils/skuDiscounts';
import ICartItem, { CartItemValidationConfig } from './ICartItem';
import sanitizeData from '~api/utils/sanitizeData';

export class CartItem implements ICartItem {
    @observable private _skuId: ICartItem['skuId'];
    @observable private _quantity: ICartItem['quantity'];
    @observable private _discountPercent: ICartItem['discountPercent'];
    @observable private _unitPriceWithTax: ICartItem['unitPriceWithTax'];
    @observable private _appliedPriceWithTax: ICartItem['appliedPriceWithTax'];
    @observable private _discountedUnitPriceWithTax: ICartItem['discountedUnitPriceWithTax'];
    @observable private is_replenishable: ICartItem['isReplenishable'];
    @observable private _enableReplenishment: ICartItem['enableReplenishment'];
    @observable public replenishmentOptions: ICartItem['replenishmentOptions'];
    @observable private is_oos: ICartItem['isOutOfStock'];
    @observable private _product: ICartItem['product'];
    @observable private replenishment_info: ICartItem['replenishmentInfo'];
    @observable public offerCode: string | null;
    @observable public appliedPrice: number;
    @observable public discountType: DiscountTypes | null;

    @computed
    public get skuId(): ICartItem['skuId'] {
        return this._skuId;
    }
    public set skuId(skuId: ICartItem['skuId']) {
        this._skuId = skuId;
    }

    @computed
    public get discountPercent(): ICartItem['discountPercent'] {
        return this._discountPercent;
    }
    public set discountPercent(discountPercent: ICartItem['discountPercent']) {
        this._discountPercent = discountPercent;
    }

    @computed
    public get quantity(): ICartItem['quantity'] {
        return this._quantity;
    }
    public set quantity(quantity: ICartItem['quantity']) {
        this._quantity = Number(quantity);
    }

    @computed
    public get product(): ICartItem['product'] {
        return this._product;
    }
    public set product(product: ICartItem['product']) {
        this._product = product;
    }

    @computed
    public get unitPriceWithTax(): ICartItem['unitPriceWithTax'] {
        return this._unitPriceWithTax;
    }

    public set unitPriceWithTax(unitPriceWithTax: ICartItem['unitPriceWithTax']) {
        this._unitPriceWithTax = unitPriceWithTax;
    }

    @computed
    public get discountedUnitPriceWithTax(): ICartItem['discountedUnitPriceWithTax'] {
        return this._discountedUnitPriceWithTax;
    }
    public set discountedUnitPriceWithTax(
        discountedUnitPriceWithTax: ICartItem['discountedUnitPriceWithTax']
    ) {
        this._discountedUnitPriceWithTax = discountedUnitPriceWithTax;
    }

    @computed
    public get appliedPriceWithTax(): ICartItem['appliedPriceWithTax'] {
        return this._appliedPriceWithTax;
    }
    public set appliedPriceWithTax(appliedPriceWithTax: ICartItem['appliedPriceWithTax']) {
        this._appliedPriceWithTax = appliedPriceWithTax;
    }

    @computed
    public get enableReplenishment(): ICartItem['enableReplenishment'] {
        return this._enableReplenishment;
    }
    public set enableReplenishment(enableReplenishment: ICartItem['enableReplenishment']) {
        this._enableReplenishment = enableReplenishment;
    }

    @computed
    public get replenishment_options(): ICartItem['replenishmentOptions'] {
        return this.replenishmentOptions;
    }
    public set replenishment_options(replenishmentOptions: ICartItem['replenishmentOptions']) {
        this.replenishmentOptions = replenishmentOptions;
    }

    @computed
    public get isReplenishable(): ICartItem['isReplenishable'] {
        return this.is_replenishable;
    }
    public set isReplenishable(is_replenishable: ICartItem['isReplenishable']) {
        this.is_replenishable = is_replenishable;
    }
    @computed
    public get isOutOfStock(): ICartItem['isOutOfStock'] {
        return this.is_oos;
    }
    public set isOutOfStock(is_oos: ICartItem['isOutOfStock']) {
        this.is_oos = is_oos;
    }

    @computed
    public get replenishmentInfo(): ICartItem['replenishmentInfo'] {
        return this.replenishment_info;
    }
    public set replenishmentInfo(replenishmentInfo: ICartItem['replenishmentInfo']) {
        this.replenishment_info = replenishmentInfo;
    }

    public static fromJSON(data: ICartItem) {
        const item = new CartItem();
        // @ts-ignore
        sanitizeData(data, CartItemValidationConfig);
        set(item, data);

        return item;
    }
}
